import { useMemo, useState, useEffect, useCallback } from "react";
import { useSelection } from "../../hooks/use-selection";
import * as actions from "../../layouts/dashboard/actions";
import {
  LPOREPORT_ENDPOINT,
  LSOREPORT_ENDPOINT,
  CATEGORYREPORT_ENDPOINT,
  CATEGORY_PRODUCTS_REPORT_ENDPOINT,
} from "../../utils/constants";
import BasePageConfigs from "../page-configs";
import BasePage from "../page-blueprint";
import { useLoadData } from "../../services/featching-data-service";
import { ReportTable } from "../../sections/report/report-table";
import { ReportSearch } from "../../sections/report/report-search";
import { useLocation, useParams } from "react-router-dom";

const generateFiscalYears = () => {
  const startYearForPeriods = 2023;

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  let maxStartYearForCompletedPeriod;

  if (currentMonth >= 6) {
    maxStartYearForCompletedPeriod = currentYear - 1;
  } else {
    maxStartYearForCompletedPeriod = currentYear - 2;
  }

  if (maxStartYearForCompletedPeriod < startYearForPeriods) {
    maxStartYearForCompletedPeriod = startYearForPeriods - 1;
  }

  const years = [];
  for (
    let year = startYearForPeriods;
    year <= maxStartYearForCompletedPeriod;
    year++
  ) {
    years.push(`${year}/${year + 1}`);
  }
  return years;
};

const useProductIds = (reports, type) => {
  return useMemo(() => {
    if (!reports || type === "category") {
      return [];
    }
    return reports.map((report) => report.product?.id || report.id);
  }, [reports, type]);
};

const ReportPage = () => {
  const {
    page,
    rowsPerPage,
    handlePageChange,
    handleRowsPerPageChange,
    componentRef,
  } = BasePageConfigs();

  const location = useLocation();
  const params = useParams();

  const [reportType, setReportType] = useState("");

  const fiscalYears = useMemo(() => generateFiscalYears(), []);
  const initialPeriod =
    fiscalYears.length > 0 ? fiscalYears[fiscalYears.length - 1] : "";
  const [selectedPeriod, setSelectedPeriod] = useState(initialPeriod);

  useEffect(() => {
    const path = location.pathname;
    if (path.includes("/lpo-report")) {
      setReportType("lpo");
    } else if (path.includes("/lso-report")) {
      setReportType("lso");
    } else if (path.includes("/category-products-report")) {
      setReportType("category-products");
    } else if (path.includes("/category-report")) {
      setReportType("category");
    } else {
      setReportType("");
    }
  }, [location.pathname]);

  const memoizedBasePayload = useMemo(() => {
    const payload = {
      offset: page * rowsPerPage,
      per_page: rowsPerPage,
    };

    if (reportType === "category-products") {
      payload.category = params.category;
      const period = params["*"];
      if (period) {
        const [from, to] = period.split("/");
        payload.from_year = from;
        payload.to_year = to;
      }
      payload.sort_order = "active_orders:desc";
    } else {
      payload.period = selectedPeriod;
    }

    return payload;
  }, [page, rowsPerPage, reportType, params.category, params.period, selectedPeriod]);

  const endpoint =
    reportType === "lso"
      ? LSOREPORT_ENDPOINT
      : reportType === "lpo"
        ? LPOREPORT_ENDPOINT
        : reportType === "category"
          ? CATEGORYREPORT_ENDPOINT
          : reportType === "category-products"
            ? CATEGORY_PRODUCTS_REPORT_ENDPOINT
            : null;

  const {
    data: reports,
    totals,
    isLoading,
    setTotals,
    setData,
    setIsLoading,
  } = useLoadData(
    memoizedBasePayload,
    endpoint,
    reportType === "category-products" ? "active_orders" : "id",
    "desc"
  );

  const reportIds = useProductIds(reports, reportType);
  const reportsSelection = useSelection(reportIds);

  const titles = {
    lso: `FINANCIAL YEAR ${selectedPeriod} SERVICES REPORT`,
    lpo: `FINANCIAL YEAR ${selectedPeriod} INVENTORY REPORT`,
    category: `FINANCIAL YEAR ${selectedPeriod} CATEGORY REPORT`,
    "category-products": `${params.category || ""} PRODUCTS REPORT`.toUpperCase(),
  };

  const title = titles[reportType] || "";

  const handlePeriodChange = useCallback((newPeriod) => {
    setSelectedPeriod(newPeriod);
  }, []);

  return BasePage(
    title,
    null,
    reportType !== "category-products" ? (
      <ReportSearch
        endpoint={endpoint}
        payload={memoizedBasePayload}
        setTotals={setTotals}
        setIsLoading={setIsLoading}
        setData={setData}
        type={reportType}
        fiscalYears={fiscalYears}
        currentSelectedPeriod={selectedPeriod}
        onPeriodChange={handlePeriodChange}
        data={reports}
      />
    ) : null,
    <ReportTable
      count={totals}
      items={reports}
      onDeselectAll={reportsSelection.handleDeselectAll}
      onDeselectOne={reportsSelection.handleDeselectOne}
      onPageChange={handlePageChange}
      onRowsPerPageChange={handleRowsPerPageChange}
      onSelectAll={reportsSelection.handleSelectAll}
      onSelectOne={reportsSelection.handleSelectOne}
      page={page}
      rowsPerPage={rowsPerPage}
      selected={reportsSelection.selected}
      isLoading={isLoading}
      componentRef={componentRef}
      type={reportType}
      selectedPeriod={selectedPeriod}
    />,
    componentRef,
    actions.canAddInventory
  );
};

export default ReportPage;
